import { useNavigate } from 'react-router-dom';
import StatusBadge from '@/components/base/StatusBadge';
import Badge from '@/components/base/Badge';
import type { Campaign } from '@/types/portal';
import { formatMoney } from '@/utils/format';

export default function CampaignProgressTable({ rows }: { rows: Campaign[] }) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <table className="row-accent w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold">Progress</th>
            <th className="px-3 py-3 font-semibold">Creators</th>
            <th className="px-3 py-3 font-semibold">Spend</th>
            <th className="px-5 py-3 text-right font-semibold">Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((campaign) => (
            <tr
              key={campaign.id}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
              className="cursor-pointer border-b border-background-100 last:border-0 hover:bg-background-100"
            >
              <td className="px-5 py-3">
                <p className="text-sm font-medium text-foreground-950">{campaign.name}</p>
                <p className="text-xs text-foreground-500">
                  {[campaign.brandClient, campaign.markets.join(', ')].filter(Boolean).join(' · ')}
                </p>
              </td>
              <td className="px-3 py-3">
                <StatusBadge status={campaign.status} />
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-primary-500 animate-grow-x transition-[width] duration-700"
                      style={{ width: `${campaign.progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-foreground-600">{campaign.progress}%</span>
                </div>
              </td>
              <td className="px-3 py-3 text-sm text-foreground-700">
                {campaign.creatorsActive}/{campaign.creatorsTotal || '—'}
              </td>
              <td className="px-3 py-3 text-sm text-foreground-700">
                {formatMoney(campaign.spend, campaign.currency)}
                <span className="block text-[11px] text-foreground-400">
                  of {formatMoney(campaign.budget, campaign.currency)}
                </span>
              </td>
              <td className="px-5 py-3 text-right">
                {campaign.flags.length > 0 ? (
                  <Badge tone="primary" icon="ri-flag-line">
                    {campaign.flags[0]}
                  </Badge>
                ) : (
                  <span className="text-xs text-foreground-400">Clear</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}